import { inject, Injectable } from '@angular/core';

import { Store } from '@ngrx/store';

import { OfferStatus } from '@core/offers-types';

import { JobsWrapperSelectors } from './jobs-wrapper.selectors';
import { JobsFullSelectors } from '../jobs-full/jobs-full.store';

@Injectable({
  providedIn: 'root',
})
export class JobsWrapperDataService {
  public static jobItemByIdSelector = JobsWrapperSelectors.jobItemById;
  public static activeJobItemsSelector = JobsWrapperSelectors.activeJobItems;

  private readonly store = inject(Store);

  public getJobFullById$(id: number) {
    return this.store.select(JobsFullSelectors.byId(id));
  }

  public getJobFullById(id: number) {
    return this.store.selectSignal(JobsFullSelectors.byId(id));
  }

  public getJobFullByIdWithReference$(id: number) {
    return this.store.select(JobsFullSelectors.byIdWithReferences(id));
  }

  public getJobFullBySlug$(slug: string) {
    return this.store.select(JobsFullSelectors.bySlug(slug));
  }

  public getJobFullBySlugWithReference$(slug: string) {
    return this.store.select(JobsFullSelectors.bySlugWithReferences(slug));
  }

  public getIsJobFullPendingByIdOrSlug$(idOrSlug?: number | string) {
    return this.store.select(JobsFullSelectors.isPending(idOrSlug));
  }

  public getIsJobFullLoadedByIdOrSlug$(idOrSlug: number | string) {
    return this.store.select(JobsFullSelectors.isLoaded(idOrSlug));
  }

  public getJobFullErrorById$(id: number) {
    return this.store.select(JobsFullSelectors.error(id));
  }

  public getIsJobFullFailedByIdOrSlug$(idOrSlug: number | string) {
    return this.store.select(JobsFullSelectors.error(idOrSlug));
  }

  public getJobFullMetaByIdOrSlug$(idOrSlug: number | string) {
    return this.store.select(JobsFullSelectors.meta(idOrSlug));
  }

  public getJobItems$() {
    return this.store.select(JobsWrapperSelectors.jobItems);
  }

  public getJobItems() {
    return this.store.selectSignal(JobsWrapperSelectors.jobItems);
  }

  public getActiveJobItems$() {
    return this.store.select(JobsWrapperSelectors.activeJobItems);
  }

  public getActiveJobItems() {
    return this.store.selectSignal(JobsWrapperSelectors.activeJobItems);
  }

  public getArchivedJobItems$() {
    return this.store.select(JobsWrapperSelectors.archivedJobItems);
  }

  public getJobItemsIdMap$() {
    return this.store.select(JobsWrapperSelectors.jobItemsIdMap);
  }

  public getJobItemsIdMap() {
    return this.store.selectSignal(JobsWrapperSelectors.jobItemsIdMap);
  }

  public getJobItemById$(id: number) {
    return this.store.select(JobsWrapperSelectors.jobItemById(id));
  }

  public getJobItemById(id: number) {
    return this.store.selectSignal(JobsWrapperSelectors.jobItemById(id));
  }

  public getJobItemsByIds$(ids: number[]) {
    return this.store.select(JobsWrapperSelectors.jobItemsByIds(ids));
  }

  public getActiveJobItemsIsLoaded$() {
    return this.store.select(JobsWrapperSelectors.activeJobItemsIsLoaded);
  }

  public getActiveJobItemsByNotStatus$(statuses: OfferStatus[]) {
    return this.store.select(JobsWrapperSelectors.activeJobItemsByNotStatus(statuses));
  }

  public getActiveJobItemsByNotStatus(statuses: OfferStatus[]) {
    return this.store.selectSignal(JobsWrapperSelectors.activeJobItemsByNotStatus(statuses));
  }

  public getActiveJobItemsByStatus$(statuses: OfferStatus[]) {
    return this.store.select(JobsWrapperSelectors.activeJobItemsByStatus(statuses));
  }

  public getActiveJobItemsCanAssignTo$() {
    return this.store.select(JobsWrapperSelectors.activeJobItemsCanAssignTo);
  }

  public getActiveJobItemsCanAssignTo() {
    return this.store.selectSignal(JobsWrapperSelectors.activeJobItemsCanAssignTo);
  }

  public getJobItemBySlug$(slug: string) {
    return this.store.select(JobsWrapperSelectors.jobItemBySlug(slug));
  }
}
